import { RegionCode, CurrencyCode } from '@/contexts/RegionContext'

/**
 * pricing.ts — Configuración centralizada de planes y precios
 *
 * FUENTE DE VERDAD para display en frontend (landing + billing).
 * Los límites funcionales reales viven en backend/src/config/planLimits.ts
 *
 * Planes vigentes: free | starter | smart | pro | elite
 * Períodos: mensual | semestral
 *
 * Al agregar un plan nuevo actualizar:
 *   1. PLANS array (features, cta, popular, elite)
 *   2. PRICING_BY_REGION (precios MX y US, mensual y semestral)
 *   3. planLimits.ts en backend (límites funcionales reales)
 *   4. stripe.service.ts PRICE_IDS (price IDs de Stripe)
 */

export interface PlanPricing {
  price: number | null
  currency: CurrencyCode
  period: string
  planId: string
  semestralPrice?: number | null
  semestralPlanId?: string
  semestralPeriod?: string
}

export interface Plan {
  id: string
  name: string
  description: string
  features: string[]
  popular: boolean
  elite: boolean
  starter?: boolean
  cta: string
}

export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Prueba gratuita',
    description: '7 días para descubrir tu potencial',
    features: [
      '3 propiedades publicadas',
      '3 fotos por propiedad',
      'Agente IA atiende y califica tus leads 24/7',
      '10 conversaciones IA incluidas',
      'CRM para gestionar tus leads',
      'Analytics de visitas',
      'QR codes para tus propiedades',
      '1 template profesional',
    ],
    cta: 'Comenzar gratis',
    popular: false,
    elite: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    description: 'Tu presencia web profesional sin IA',
    features: [
      '10 propiedades publicadas',
      '5 fotos por propiedad',
      'QR codes para compartir propiedades',
      '3 templates profesionales',
      '30 MB de almacenaje',
      'Soporte por email',
      'Sin agente IA — tú atiendes directamente',
      'Sin CRM de leads',
    ],
    cta: 'Elegir Starter',
    popular: false,
    elite: false,
    starter: true,
  },
  {
    id: 'smart',
    name: 'Smart',
    description: 'IA + CRM para crecer inteligentemente',
    features: [
      '25 propiedades publicadas',
      '10 fotos por propiedad',
      'Agente IA califica leads 24/7 — 90 conversaciones/mes',
      'CRM completo de leads',
      'Analytics de visitas y conversiones',
      'QR codes para tus propiedades',
      '5 templates profesionales',
      '5 documentos de entrenamiento IA',
      '50 MB de almacenaje',
      'Soporte por email',
    ],
    cta: 'Elegir Smart',
    popular: false,
    elite: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'El arsenal completo del agente moderno',
    features: [
      '50 propiedades publicadas',
      '15 fotos por propiedad',
      'Agente IA califica leads 24/7 — 250 conversaciones/mes',
      'CRM completo + Analytics avanzados',
      'Marketing Suite IA — fichas, videos y posts con IA',
      '100 créditos de marketing incluidos/mes',
      '8 templates profesionales',
      '10 documentos de entrenamiento IA',
      '100 MB de almacenaje',
      'QR codes para tus propiedades',
      'Soporte por email',
    ],
    cta: 'Elegir Pro',
    popular: true,
    elite: false,
  },
  {
    id: 'elite',
    name: 'Elite',
    description: 'Para agencias y equipos de alto volumen',
    features: [
      'Hasta 1,000 propiedades publicadas',
      '50 fotos por propiedad',
      'Agente IA califica leads 24/7 — 1,000 conversaciones/mes',
      'CRM completo + Analytics avanzados',
      'Marketing Suite IA con créditos negociados',
      'Todos los templates + templates exclusivos a tu marca',
      '50 documentos de entrenamiento IA',
      '500 MB de almacenaje',
      'Onboarding y soporte prioritario',
      'Gestión multi-agente para tu equipo',
    ],
    cta: 'Contactar ventas',
    popular: false,
    elite: true,
  },
]

const PRICING_BY_REGION: Record<RegionCode, Record<string, PlanPricing>> = {
  MX: {
    free: {
      price: 0,
      currency: 'MXN',
      period: '7 días',
      planId: 'trial',
    },
    starter: {
      price: 379,
      currency: 'MXN',
      period: 'mes',
      planId: 'starter_mx',
      semestralPrice: 1999,
      semestralPlanId: 'starter_mx_6m',
      semestralPeriod: '6 meses',
    },
    smart: {
      price: 789,
      currency: 'MXN',
      period: 'mes',
      planId: 'smart_mx',
      semestralPrice: 3999,
      semestralPlanId: 'smart_mx_6m',
      semestralPeriod: '6 meses',
    },
    pro: {
      price: 1489,
      currency: 'MXN',
      period: 'mes',
      planId: 'pro_mx',
      semestralPrice: 7499,
      semestralPlanId: 'pro_mx_6m',
      semestralPeriod: '6 meses',
    },
    elite: {
      price: null,
      currency: 'MXN',
      period: '',
      planId: 'elite',
    },
  },
  US: {
    free: {
      price: 0,
      currency: 'USD',
      period: '7 days',
      planId: 'trial',
    },
    starter: {
      price: 25,
      currency: 'USD',
      period: 'month',
      planId: 'starter_us',
      semestralPrice: 130,
      semestralPlanId: 'starter_us_6m',
      semestralPeriod: '6 months',
    },
    smart: {
      price: 50,
      currency: 'USD',
      period: 'month',
      planId: 'smart_us',
      semestralPrice: 260,
      semestralPlanId: 'smart_us_6m',
      semestralPeriod: '6 months',
    },
    pro: {
      price: 80,
      currency: 'USD',
      period: 'month',
      planId: 'pro_us',
      semestralPrice: 420,
      semestralPlanId: 'pro_us_6m',
      semestralPeriod: '6 months',
    },
    elite: {
      price: null,
      currency: 'USD',
      period: '',
      planId: 'elite',
    },
  },
}

export const getPlanPricing = (
  planId: string,
  region: RegionCode
): PlanPricing => {
  return (
    PRICING_BY_REGION[region]?.[planId] ||
    PRICING_BY_REGION['MX'][planId]
  )
}

export const getPlansWithPricing = (region: RegionCode) => {
  return PLANS.map(plan => ({
    ...plan,
    pricing: getPlanPricing(plan.id, region)
  }))
}

export const formatPrice = (
  price: number | null,
  currency: CurrencyCode
): string => {
  if (price === null) return 'Contactar'
  if (price === 0) return '$0'
  return new Intl.NumberFormat(currency === 'MXN' ? 'es-MX' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)
}

export const getCurrencySymbol = (currency: CurrencyCode): string =>
  currency === 'MXN' ? 'MXN' : 'USD'

export const getSemestralSavings = (
  planId: string,
  region: RegionCode
): number | null => {
  const pricing = getPlanPricing(planId, region)
  if (!pricing.price || !pricing.semestralPrice) return null
  const monthlyEquivalent = pricing.semestralPrice / 6
  return Math.round(pricing.price - monthlyEquivalent)
}
